import { useEffect, useState } from 'react';
import { apiFetch, detailMessage } from '../api';

/* One user's hourly call cap on one MCP server, saved on its own.

   It lives inside the user form but is NOT part of it: the cap is stored on the grant
   row, so it is saved the moment "Save" is pressed here, and the form's own save
   neither sends nor resets it. proxy-tools picks it up within seconds, with no deploy.

   Only a server the user ALREADY holds can carry a cap. A toggle switched on in this
   form but not yet saved is not a grant yet, so the cell shows the cap the server would
   start with and how to change it, instead of offering a field that would be refused.
   It used to show a bare dash, which beside another server's field read as "this server
   has no rate limit". Switching a server off and saving deletes the grant, and its cap
   with it. */
function McpRateLimitCell({ userId, server, maxRate, canEdit, onSaved, label }) {
  const saved = server.rate_limit_per_hour;
  const [draft, setDraft] = useState(saved == null ? '' : String(saved));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setDraft(saved == null ? '' : String(saved)); }, [saved]);

  if (!server.granted) {
    return (
      <div className="stack gap-1">
        <span className="text-sm">Default: {server.default_rate_limit_per_hour} calls / hour</span>
        <span className="text-xs text-muted">
          Save the user with this server on to set a custom cap.
        </span>
      </div>
    );
  }

  const trimmed = draft.trim();
  const value = trimmed === '' ? null : Number(trimmed);
  const invalid = value !== null && !(Number.isInteger(value) && value >= 1 && value <= maxRate);
  const dirty = value !== saved;

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await apiFetch(`/api/mcp/users/${userId}/rate-limits/${server.tool_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rate_limit_per_hour: value }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(detailMessage(body.detail, 'Could not save the cap'));
      onSaved(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Enter in this field must save THIS value, not submit the user form around it.
  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (canEdit && dirty && !invalid && !saving) save();
    }
  };

  return (
    <div className="stack gap-1">
      <div className="row gap-1">
        <input
          type="number"
          inputMode="numeric"
          min={1}
          max={maxRate}
          step={1}
          className="form-input num"
          style={{ width: 96 }}
          value={draft}
          placeholder={`${server.default_rate_limit_per_hour}`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={!canEdit || saving}
          aria-label={label}
          aria-invalid={invalid || undefined}
        />
        <span className="text-sm text-muted">calls / hour</span>
        {canEdit && dirty && (
          <button type="button" className="btn btn-secondary btn-sm"
                  onClick={save} disabled={invalid || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        )}
      </div>
      <span className="text-xs text-muted">
        {saved == null
          ? `Default: ${server.default_rate_limit_per_hour} calls / hour`
          : `Custom · default ${server.default_rate_limit_per_hour} calls / hour`}
      </span>
      {invalid && (
        <span className="text-xs text-danger" role="alert">
          A whole number from 1 to {maxRate}, or empty for the default.
        </span>
      )}
      {error && <span className="text-xs text-danger" role="alert">{error}</span>}
    </div>
  );
}

export default McpRateLimitCell;
